import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface InfoTipProps {
  /** Texto para leitor de tela / usuário sem mouse — descreve o que vai abrir. */
  label: string;
  children: React.ReactNode;
  tone?: 'default' | 'warning' | 'danger';
}

const WIDTH = 260;

const TONE_ICON: Record<NonNullable<InfoTipProps['tone']>, string> = {
  default: 'text-gray-400 dark:text-gray-500',
  warning: 'text-amber-500',
  danger: 'text-red-500',
};

/**
 * Gatilho clicável/focável para informação que hoje só existia em `title`
 * (tooltip nativo — não funciona em toque, não abre por teclado, ~1s de
 * atraso). Mesmo padrão de popover ancorado do `DeltaPopover.tsx`, mas
 * genérico para texto simples.
 */
const InfoTip: React.FC<InfoTipProps> = ({ label, children, tone = 'default' }) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const h = popRef.current?.offsetHeight || 80;
    const margin = 8;
    let top = anchor.bottom + 6;
    if (top + h > window.innerHeight - margin) top = Math.max(margin, anchor.top - h - 6);
    let left = anchor.left;
    if (left + WIDTH > window.innerWidth - margin) left = window.innerWidth - WIDTH - margin;
    if (left < margin) left = margin;
    setPos({ top, left });
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        onClick={e => {
          e.stopPropagation();
          setAnchor(btnRef.current!.getBoundingClientRect());
          setOpen(v => !v);
        }}
        className={`inline-flex align-middle ml-0.5 ${TONE_ICON[tone]} hover:opacity-70`}
      >
        <Info size={11} />
      </button>
      {open &&
        anchor &&
        createPortal(
          <div className="fixed inset-0 z-[2100]" onClick={() => setOpen(false)}>
            <div
              ref={popRef}
              style={{ top: pos.top, left: pos.left, width: WIDTH }}
              className="fixed bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 p-3 text-xs leading-snug text-gray-600 dark:text-gray-300 animate-fade-in"
              onClick={e => e.stopPropagation()}
            >
              {children}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default InfoTip;
